import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Chip,
  IconButton,
  InputAdornment,
  RadioGroup,
  Radio,
  Divider,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  LocalHospital as LocalHospitalIcon,
  Psychology as PsychologyIcon,
  Restaurant as RestaurantIcon,
  MedicalServices as MedicalServicesIcon,
  HealthAndSafety as HealthAndSafetyIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { followUpService, FollowUpListItem, FollowUpCompletionData } from '../services/followUpService';

interface CompleteFollowUpDialogProps {
  open: boolean;
  onClose: () => void;
  followUp: FollowUpListItem;
  onSuccess: () => void;
}

const CompleteFollowUpDialog: React.FC<CompleteFollowUpDialogProps> = ({
  open,
  onClose,
  followUp,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completionDate, setCompletionDate] = useState<Date | null>(new Date());
  
  // Communication methods
  const [emailedPatient, setEmailedPatient] = useState(false);
  const [spokeOnPhone, setSpokeOnPhone] = useState(false);
  const [metInPerson, setMetInPerson] = useState(false);
  
  // Referrals offered
  const [referralCounselingCenter, setReferralCounselingCenter] = useState(false);
  const [referralTeamPhysician, setReferralTeamPhysician] = useState(false);
  const [referralNutritionist, setReferralNutritionist] = useState(false);
  const [referralPrimaryCarePhysician, setReferralPrimaryCarePhysician] = useState(false);
  const [referralExternalMentalHealthProvider, setReferralExternalMentalHealthProvider] = useState(false);
  
  // Other resources
  const [otherResourceInput, setOtherResourceInput] = useState('');
  const [otherResources, setOtherResources] = useState<string[]>([]);
  
  // Patient response
  const [patientResponse, setPatientResponse] = useState<'accepted' | 'declined' | ''>('');
  
  // Notes
  const [completionNotes, setCompletionNotes] = useState('');

  const handleAddOtherResource = () => {
    if (otherResourceInput.trim()) {
      setOtherResources([...otherResources, otherResourceInput.trim()]);
      setOtherResourceInput('');
    }
  };

  const handleRemoveOtherResource = (index: number) => {
    setOtherResources(otherResources.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    // Validation
    if (!completionDate) {
      setError('Please select a completion date');
      return;
    }

    if (!emailedPatient && !spokeOnPhone && !metInPerson) {
      setError('Please select at least one communication method');
      return;
    }

    if (patientResponse === '') {
      setError('Please indicate patient response');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: FollowUpCompletionData = {
        completionDate: completionDate.toISOString().split('T')[0],
        emailedPatient,
        spokeOnPhone,
        metInPerson,
        referralCounselingCenter,
        referralTeamPhysician,
        referralNutritionist,
        referralPrimaryCarePhysician,
        referralExternalMentalHealthProvider,
        otherResources,
        patientAcceptedReferral: patientResponse === 'accepted',
        patientDeclinedReferral: patientResponse === 'declined',
        completionNotes
      };

      await followUpService.completeFollowUp(followUp.id, data);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error completing follow-up:', error);
      setError('Failed to complete follow-up. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Complete Follow-up</Typography>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Patient Info */}
          <Alert severity="info" sx={{ mb: 3 }}>
            Completing follow-up for: <strong>{followUp.patientName}</strong> (ID: {followUp.schoolId})
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Completion Date */}
          <Box sx={{ mb: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Completion Date"
                value={completionDate}
                onChange={(newValue) => setCompletionDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true
                  }
                }}
              />
            </LocalizationProvider>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Communication Methods */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Communication Methods (select all that apply) *
          </Typography>
          <FormGroup sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={emailedPatient}
                  onChange={(e) => setEmailedPatient(e.target.checked)}
                  icon={<EmailIcon />}
                  checkedIcon={<EmailIcon />}
                />
              }
              label="Emailed patient"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={spokeOnPhone}
                  onChange={(e) => setSpokeOnPhone(e.target.checked)}
                  icon={<PhoneIcon />}
                  checkedIcon={<PhoneIcon />}
                />
              }
              label="Spoke on phone"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={metInPerson}
                  onChange={(e) => setMetInPerson(e.target.checked)}
                  icon={<PersonIcon />}
                  checkedIcon={<PersonIcon />}
                />
              }
              label="Met in person"
            />
          </FormGroup>

          <Divider sx={{ my: 3 }} />

          {/* Referrals Offered */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Referrals Offered (select all that apply)
          </Typography>
          <FormGroup sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={referralCounselingCenter}
                  onChange={(e) => setReferralCounselingCenter(e.target.checked)}
                  icon={<PsychologyIcon />}
                  checkedIcon={<PsychologyIcon />}
                />
              }
              label="Counseling Center"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={referralTeamPhysician}
                  onChange={(e) => setReferralTeamPhysician(e.target.checked)}
                  icon={<LocalHospitalIcon />}
                  checkedIcon={<LocalHospitalIcon />}
                />
              }
              label="Team Physician"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={referralNutritionist}
                  onChange={(e) => setReferralNutritionist(e.target.checked)}
                  icon={<RestaurantIcon />}
                  checkedIcon={<RestaurantIcon />}
                />
              }
              label="Nutritionist"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={referralPrimaryCarePhysician}
                  onChange={(e) => setReferralPrimaryCarePhysician(e.target.checked)}
                  icon={<MedicalServicesIcon />}
                  checkedIcon={<MedicalServicesIcon />}
                />
              }
              label="Primary Care Physician"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={referralExternalMentalHealthProvider}
                  onChange={(e) => setReferralExternalMentalHealthProvider(e.target.checked)}
                  icon={<HealthAndSafetyIcon />}
                  checkedIcon={<HealthAndSafetyIcon />}
                />
              }
              label="External Mental Health Provider"
            />
          </FormGroup>

          {/* Other Resources */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Other Resources
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter other resources offered..."
              value={otherResourceInput}
              onChange={(e) => setOtherResourceInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddOtherResource();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleAddOtherResource}>
                      <AddIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {otherResources.map((resource, index) => (
                <Chip
                  key={index}
                  label={resource}
                  onDelete={() => handleRemoveOtherResource(index)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Patient Response */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Patient Response *
          </Typography>
          <RadioGroup
            value={patientResponse}
            onChange={(e) => setPatientResponse(e.target.value as 'accepted' | 'declined')}
            sx={{ mb: 3 }}
          >
            <FormControlLabel
              value="accepted"
              control={<Radio />}
              label="Patient accepted referral(s)"
            />
            <FormControlLabel
              value="declined"
              control={<Radio />}
              label="Patient declined referral(s)"
            />
          </RadioGroup>

          <Divider sx={{ my: 3 }} />

          {/* Completion Notes */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Add any additional notes about this follow-up..."
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Completing...' : 'Complete Follow-up'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteFollowUpDialog;